import { AppIcon, type IconKind } from '@/components/AppIcon';
import { formatMoney, formatNumber } from '@/format';
import { RUNNING_SHORT, type ScreenIntent } from '@/stores/shellNav';
import type { Overview } from './useOverview';
import type { WidgetDef } from './widgets';

/**
 * The row of figures across the top: one number each, in its module's colour,
 * and a click that opens the screen behind it.
 *
 * Every card is built from the one overview response, so a card cannot
 * disagree with the chart below it.
 */

export type CardTone = 'plain' | 'good' | 'warn' | 'alert';

export interface CardContent {
  label: string;
  value: string;
  note: string;
  icon: IconKind;
  tone: CardTone;
  /** The screen this card opens, or undefined for a card that only reads. */
  screen?: string;
  /** What that screen should show on arrival, when not everything. */
  intent?: ScreenIntent;
  /** For the one card that scrolls rather than navigates. */
  scrollTo?: string;
}

/** What each card shows, given what the server sent. Pure, so it is testable. */
export function cardContent(
  id: string,
  overview: Overview,
  waiting: number,
): CardContent | null {
  const sections = overview.sections;

  switch (id) {
    case 'stock-value': {
      const stock = sections.stock;
      if (!stock) return null;
      // Without permission to see costs the card still earns its place by
      // saying how many items are held.
      return overview.canSee.stockValue
        ? {
            label: 'Stock value',
            value: formatMoney(stock.stockValue),
            note: `${formatNumber(stock.skusTracked, 0)} items in stock`,
            icon: 'value',
            tone: 'plain',
            screen: 'inv-stock',
          }
        : {
            label: 'Items in stock',
            value: formatNumber(stock.skusTracked, 0),
            note: 'Across the stores you can see',
            icon: 'stock',
            tone: 'plain',
            screen: 'inv-stock',
          };
    }

    case 'low-stock': {
      const stock = sections.stock;
      if (!stock) return null;
      return {
        label: 'Running short',
        value: formatNumber(stock.belowThreshold, 0),
        note: stock.belowThreshold > 0 ? 'Below the reorder level' : 'Everything above its level',
        icon: 'attention',
        tone: stock.belowThreshold > 0 ? 'alert' : 'good',
        // The count's own items, not all stock. At zero it still opens the
        // list, which then says plainly that nothing is short.
        screen: 'inv-stock',
        intent: RUNNING_SHORT,
      };
    }

    case 'expiring': {
      const stock = sections.stock;
      if (!stock) return null;
      return {
        label: 'Expiring soon',
        value: formatNumber(stock.expiringSoon, 0),
        note: stock.expiringSoon > 0 ? 'Batches near their expiry date' : 'Nothing expiring',
        icon: 'expiring',
        tone: stock.expiringSoon > 0 ? 'warn' : 'good',
        screen: 'inv-stock',
      };
    }

    case 'farms': {
      const farms = sections.farms;
      if (!farms) return null;
      const capacity =
        farms.birdCapacity === undefined
          ? `${formatNumber(farms.sheds, 0)} sheds`
          : `${formatNumber(farms.sheds, 0)} sheds · ${formatNumber(farms.birdCapacity, 0)} birds`;
      return {
        label: 'Farms',
        value: formatNumber(farms.farms, 0),
        note: capacity,
        icon: 'farm',
        tone: 'plain',
        screen: 'mdm-dash',
      };
    }

    case 'staff': {
      const people = sections.people;
      if (!people) return null;
      return {
        label: 'People',
        value: formatNumber(people.totals.staff, 0),
        note: `${formatNumber(people.totals.monthly, 0)} monthly · ${formatNumber(people.totals.daily, 0)} daily wage`,
        icon: 'people',
        tone: 'plain',
        screen: 'hr-emp',
      };
    }

    case 'present': {
      const people = sections.people;
      if (!people) return null;
      const { present, staff, notMarked } = people.totals;
      const percent = staff > 0 ? Math.round((present / staff) * 100) : 0;
      return {
        label: 'Present today',
        value: formatNumber(present, 0),
        note:
          notMarked > 0
            ? `${formatNumber(notMarked, 0)} not marked yet`
            : staff > 0
              ? `${percent}% of ${formatNumber(staff, 0)} people`
              : 'Nobody on the list',
        icon: 'attendance',
        tone: notMarked > 0 ? 'warn' : 'good',
        screen: 'hr-att',
      };
    }

    case 'waiting':
      return {
        label: 'Waiting for you',
        value: formatNumber(waiting, 0),
        note: waiting > 0 ? 'Things needing a decision' : 'Nothing to decide',
        icon: 'checklist',
        tone: waiting > 0 ? 'warn' : 'good',
        scrollTo: 'dashboard-action-needed',
      };

    default:
      return null;
  }
}

export function HeadlineCards({
  cards,
  overview,
  waiting,
  onNavigate,
}: {
  cards: WidgetDef[];
  overview: Overview;
  waiting: number;
  onNavigate: (screenId: string, intent?: ScreenIntent) => void;
}) {
  const content = cards
    .map((card) => ({ card, content: cardContent(card.id, overview, waiting) }))
    .filter((c): c is { card: WidgetDef; content: CardContent } => c.content !== null);

  if (content.length === 0) return null;

  return (
    <div className="headlines">
      {content.map(({ card, content: c }) => {
        const go = c.screen
          ? () => onNavigate(c.screen!, c.intent)
          : c.scrollTo
            ? () => document.getElementById(c.scrollTo!)?.scrollIntoView({ block: 'center' })
            : undefined;

        const body = (
          <>
            <span className="headline__icon" aria-hidden="true">
              <AppIcon kind={c.icon} size={18} />
            </span>
            <span className="headline__label">{c.label}</span>
            <span className="headline__value">{c.value}</span>
            <span className="headline__note">{c.note}</span>
          </>
        );

        return go ? (
          <button
            key={card.id}
            type="button"
            className={`headline headline--${c.tone} headline--action`}
            onClick={go}
          >
            {body}
          </button>
        ) : (
          <div key={card.id} className={`headline headline--${c.tone}`}>
            {body}
          </div>
        );
      })}
    </div>
  );
}
